import { useRouter } from 'next/navigation';
import { select } from '@frameio/i18n';
import { getDevFlag } from '@frameio/shared/dev-flags';
import { TransferBatchStatus } from '@frameio/shared/transfers/states';
import { MenuButton, MenuGroup } from '@frameio/vapor';
import {
  IconArrowUpRight,
  IconPauseCircleFill,
  IconPlay,
  IconTrash,
  IconX,
} from '@frameio/vapor-icons';
import { useDeleteTransferBatches, useUploaderWorker } from '../../hooks';
import type { TransferBatch } from '../../types/uploader';
import { getTransferBatchHasLocalData } from '../../utils';
import TEXT from './generated/TEXT/UploaderSheetMenuContent';

const isSegmentDebuggerEnabled = getDevFlag('SEGMENT_DEBUGGER');

TEXT.DEFINE(
  {
    /** Menu button label to cancel a transfer */
    cancel: 'Cancel',
    /** Menu button label to remove a completed transfer from the list */
    removeFromList: 'Remove From List',
    /** Menu button label to navigate to the project */
    showInProject: 'Show in Project',
    /** Menu button label to view the log file */
    viewLogFile: 'View Log File',
    /** Menu button label to pause a transfer */
    pause: 'Pause',
    /** Menu button label to resume a paused transfer */
    resume: 'Resume',
    /** Dev-only menu button to force a transfer failure */
    forceFailure: 'Force Failure',
    /** Label for transfer type based on batch type */
    progressTypeLabel: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'Download',
        RENDER: 'Export',
        UPLOAD: 'Upload',
        other: '',
      }),
  },
  { select }
);

type JobControlsMenuProps = {
  job: TransferBatch;
  pathToFile: string | undefined;
};

export function UploadSheetMenuContent({
  job,
  pathToFile,
}: JobControlsMenuProps) {
  const { id, localUploadingId, status, batchType, isPaused } = job;
  const hasLocalData = getTransferBatchHasLocalData(job);
  const isJobCanceling = status === TransferBatchStatus.CANCELING;
  const isUploading = status === TransferBatchStatus.UPLOADING;
  const isDownloading = status === TransferBatchStatus.DOWNLOADING;
  const isQueued = status === TransferBatchStatus.IDLE;
  const isProcessing = status === TransferBatchStatus.PROCESSING;

  const uploader = useUploaderWorker();

  const isPausedDisabled = hasLocalData
    ? !isUploading && !isDownloading && !isPaused
    : true;

  const canCancel = hasLocalData
    ? (isQueued || isUploading || isDownloading || isProcessing) &&
      !isJobCanceling
    : false;

  const canPause = hasLocalData
    ? (isUploading || isDownloading) && !isJobCanceling
    : false;

  const canRemoveFromList =
    (!isQueued && !isUploading && !isDownloading && !isProcessing) ||
    !hasLocalData;

  const { deleteTransferBatches } = useDeleteTransferBatches();

  const router = useRouter();

  return (
    <>
      <MenuGroup>
        {canPause ? (
          <MenuButton
            data-testid="job-card--pause-button"
            disabled={isPausedDisabled}
            iconBefore={
              !isPaused ? (
                <IconPauseCircleFill data-testid="job-card--is-active" />
              ) : (
                <IconPlay data-testid="job-card--is-paused" />
              )
            }
            onPress={() => {
              if (isPaused) {
                uploader.worker.resumeJob(localUploadingId);
              } else {
                uploader.worker.pauseJob(localUploadingId);
              }
            }}
          >
            {!isPaused ? TEXT.pause : TEXT.resume}{' '}
            {TEXT.progressTypeLabel(batchType)}
          </MenuButton>
        ) : null}

        {pathToFile && (
          <MenuButton
            href={pathToFile}
            iconBefore={<IconArrowUpRight />}
            onPress={() => {
              router.push(pathToFile);
            }}
          >
            {TEXT.showInProject}
          </MenuButton>
        )}

        {canRemoveFromList && (
          <MenuButton
            data-testid="job-card--remove-button"
            iconBefore={<IconTrash />}
            onPress={() =>
              deleteTransferBatches([{ id, localJobId: localUploadingId }])
            }
          >
            {TEXT.removeFromList}
          </MenuButton>
        )}
      </MenuGroup>
      {canCancel && (
        <MenuGroup>
          <MenuButton
            data-testid="job-card--cancel-button"
            disabled={!canCancel}
            iconBefore={<IconX />}
            onPress={() => {
              uploader.worker.cancelJob(localUploadingId);
            }}
          >
            {TEXT.cancel} {TEXT.progressTypeLabel(batchType)}
          </MenuButton>

          {isSegmentDebuggerEnabled && (
            <MenuButton
              data-testid="job-card--force-failure-button"
              disabled={!canCancel}
              iconBefore={<IconX />}
              onPress={() => {
                uploader.worker.forceFailJob(localUploadingId);
              }}
            >
              {TEXT.forceFailure}
            </MenuButton>
          )}
        </MenuGroup>
      )}
    </>
  );
}
